import { useState, useEffect } from 'react';
import styles from './Themes.module.css';
import { FaDownload, FaCertificate } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Auth/AuthContext'; 
import swal from 'sweetalert2';
import { generateCertificate } from './Certificado'; 
const Themes = () => {
  const navigate = useNavigate();
  const { getToken, isAuthenticated, userData } = useAuth();
  const token = getToken(); 
  const [pasos, setPasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todosCompletados, setTodosCompletados] = useState(false);
  const [certificadoLoading, setCertificadoLoading] = useState(false);
  const [progresoPasos, setProgresoPasos] = useState({ totalPasos: 0, pasosCompletados: 0 });
  useEffect(() => {
      fetchPasos();
  }, []);

  useEffect(() => {
      const userId = localStorage.getItem('user');
      
      if (isAuthenticated && userId && !loading) {
          verificarPasosCompletados(userId);
      }
  }, [isAuthenticated, loading]);

  const fetchPasos = async () => {
      try {
          setLoading(true);
          setError(null);
          const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/obtener-pasos/${userData.id}`, 
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

          if (!response.ok) {
              throw new Error(`Error: ${response.status}`);
          }

          const data = await response.json();
          setPasos(data);
          setLoading(false);
      } catch (err) {
          console.error('Error:', err);
          setError('Error al cargar los pasos. Por favor, intenta de nuevo más tarde.');
          setLoading(false);
      }
  };

 const verificarPasosCompletados = async (userId = null) => {
      try {
          // Solo consultamos rápido si ya acabó, sin generar PDFs pesados
          const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/verificar-pasos-completados/${userData.id}`, {
              method: 'GET', 
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
              }
          });

          if (response.ok) {
              const data = await response.json();
              setTodosCompletados(data.todosCompletados);
              setProgresoPasos({
                  totalPasos: data.totalPasos,
                  pasosCompletados: data.pasosCompletados
              });
          }
      } catch (err) {
          console.error('Error al verificar pasos completados:', err);
      }
  };
  // Función para determinar si un paso está habilitado
  const isPasoHabilitado = (pasoIndex, pasos) => {
      // El primer paso siempre está habilitado
      if (pasoIndex === 0) return true;
      
      // Los pasos completados siempre están habilitados
      if (pasos[pasoIndex].uspa_completado) return true;
      
      // Un paso está habilitado si el anterior está completado
      return pasos[pasoIndex - 1].uspa_completado;
  };

  // Función para obtener el estado visual del paso
  const getPasoStatus = (pasoIndex, pasos) => {
      const paso = pasos[pasoIndex];
      
      if (paso.uspa_completado) {
          return 'completado';
      } else if (isPasoHabilitado(pasoIndex, pasos)) {
          return 'habilitado';
      } else {
          return 'deshabilitado';
      }
  };

  const handleDescargarCertificado = async () => {
      const userId = localStorage.getItem('user');
      if (!userId) return;

      try {
          setCertificadoLoading(true);
          const token = getToken();

          const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/generar-certificado/${userId}`, {
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
              }
          });

          if (response.ok) {
              const certificateData = await response.json();
              
              // Verificar que tenemos los datos necesarios
              if (!certificateData.userData) {
                  throw new Error('No se pudieron obtener los datos para el certificado');
              }

              // Generar el certificado con los datos que incluyen los resultados de los tests
              await generateCertificate(certificateData.userData);
              
              swal.fire({
                  title: '¡Felicidades!', 
                  text: 'Tu certificado ha sido generado y descargado exitosamente.',
                  icon: 'success', 
                  confirmButtonText: 'Entendido', 
                  confirmButtonColor: '#242b57',
                  customClass: { popup: 'popupbonito' }
              });
              
          } else {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Error al obtener los datos del certificado');
          }
      } catch (err) {
          console.error('Error al descargar certificado:', err);
          
          swal.fire({
              title: 'Error',
              text: err.message || 'No se pudo generar el certificado. Por favor, intenta de nuevo.',
              icon: 'error',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#242b57',
              customClass: { popup: 'popupbonito' }
          });

      } finally {
          setCertificadoLoading(false);
      }
  };

  const handleView = (pa_clave, pasoIndex) => {
      // Solo navegar si el paso está habilitado
      if (isPasoHabilitado(pasoIndex, pasos)) {
          navigate(`/paso/${pa_clave}`);
      } else {
          // Mostrar mensaje informativo si el paso no está habilitado
          swal.fire({
              title: 'Paso no disponible',
              text: 'Debes completar el paso anterior para acceder a este contenido.',
              icon: 'info',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#242b57',
              customClass: { popup: 'popupbonito' }
          });
      }
  };

  return (
    <div className={styles.temasPage}>
      <main className={styles.mainContent}>
        <section className={styles.intro}>
          <h1>Construye tu futuro</h1>
          <p>Explora cada una de las etapas clave para definir tu plan de carrera.</p>
          {isAuthenticated && progresoPasos.totalPasos > 0 && (
            <div className={styles.progresoInfo}>
              <p>Progreso: {progresoPasos.pasosCompletados} de {progresoPasos.totalPasos} pasos completados</p>
            </div>
          )}
        </section>

        {pasos.length === 0 ? (
          <div className={styles.emptyState}>
            <div>
            </div>
            <h3>No hay pasos disponibles :(</h3>
          </div>
        ) : (
          <section id="etapas" className={styles.stepsContainer}>
            {pasos.map((paso, index) => {
              const status = getPasoStatus(index, pasos);
              const habilitado = isPasoHabilitado(index, pasos);
              return (
                <div 
                  className={`${styles.stepCard} ${status === 'deshabilitado' ? styles.cardDisabled : styles[status]}`}
                  key={paso.pa_clave}
                  onClick={() => handleView(paso.pa_clave, index)}
                  title={habilitado ? paso.pa_titulo : 'Completa el paso anterior para desbloquear'}
                >
                  <h2>{paso.pa_titulo}</h2>
                  <p>{paso.pa_detalles}</p>
                </div>
              );
            })}
            
            {isAuthenticated && todosCompletados && (
              <div 
                className={styles.certificateCard}
                onClick={handleDescargarCertificado}
                title="Descargar Certificado"
              >
                <div className={styles.certificateIcon}>
                  <FaCertificate />
                </div>
                <h2>¡Felicidades!</h2>
                <p>Has completado todos los pasos. Descarga tu certificado de finalización.</p>
                <div className={styles.downloadButton}>
                  {certificadoLoading ? (
                    <span>Generando...</span>
                  ) : (
                    <>
                      <FaDownload />
                      <span>Descargar Certificado</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default Themes;